<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import { useAppBookingOptions } from '$lib/app-booking-options/app-booking-options';
	import { useFormInfo } from '$lib/form-info/form-info';
	import { useUrlQ } from '$lib/urlq';
	import classNames from 'classnames';
	import AccountModal from '../Account/AccountModal.svelte';
	import StageHeader from '../Form/StageHeader.svelte';
	import { useBookingData } from '$lib/booking-data/booking-data';
	import FeatureOnCalendar from '$routes/(app)/classes/feature-on-calendar.svelte';
	import { onMount } from 'svelte';

	const { appBookingOptions } = useAppBookingOptions();
	const { formInfo } = useFormInfo();
	const { bookingData } = useBookingData();

	let showRegisterContainer: boolean = false;

	let titles: string[] = ['Calendar', 'List'];
	$: selectedTitle = $bookingData.featureOnCalendar ? 'Calendar' : 'List';

	const featureHandler = (str: string) => {
		if (str === 'Calendar') $bookingData.featureOnCalendar = true;
		else $bookingData.featureOnCalendar = false;
	};

	function handleBackButton(event: any) {
		// Your code to handle the back button click
		if ($page.url.pathname.includes('form')) {
			goBack();
		}
	}

	onMount(() => {
		window.addEventListener('popstate', handleBackButton);

		return () => {
			window.removeEventListener('popstate', handleBackButton);
		};
	});

	const goBack = () => {
		if ($page.url.pathname.includes('confirm')) {
			if(($formInfo.classId && $formInfo.seatsCount && $formInfo.seatsTaken && $formInfo.seatsTaken !== 0 && $formInfo.seatsCount - $formInfo.seatsTaken <= 0)) {
				goto(`/form/students`);
				return;
			} else if ($formInfo.collectDatesAndTimePreferences || $formInfo.collectStartDate) {
				goto(`/form/preferences`);
			} else if ($formInfo.collectStudentInfo) {
				goto(`/form/students`);
			} else {
				goto(`/form/registration`);
			}
		} else if ($page.url.pathname.includes('students')) {
			goto(`/form/registration`);
		} else if ($page.url.pathname.includes('registration')) {
			if ($bookingData.location_id) {
				goto(
					`/classes?location=${$bookingData.location_id}
					&view=${$bookingData.featureOnCalendar ? 'calendar' : 'list'}
					${$bookingData.semester_id ? '&semester=' + $bookingData.semester_id : ''}
					`
				);
			} else {
				goto('/');
			}
		} else if ($page.url.pathname.includes('preferences')) {
			if ($formInfo.collectStudentInfo) {
				goto(`/form/students`);
			} else {
				goto(`/form/registration`);
			}
		}
	};

	// route detect
	$: isFormPages = !!$page.route.id?.startsWith(`/(app)/form`);
	$: isFinalPage = !!$page.route.id?.startsWith(`/(app)/form/final`);
	$: isSigninPage = !!$page.route.id?.startsWith(`/(app)/form/signin`);
	$: isClassPage = !!$page.route.id?.startsWith(`/(app)/classes`);
</script>

<header class="flex-none flex items-center px-4">
	<div
		class={classNames(
			'flex items-center gap-7 justify-between w-full',
			isFinalPage && '!justify-end'
		)}
	>
		{#if !isFormPages}
			<img class="w-[150px] md:w-[250px]" src="/images/logo-purple.png" alt="logo" />
		{/if}
		{#if isFormPages}
			<div class="header-navigation">
				{#if !isFinalPage}
					<div class="flex items-center gap-2">
						<button class="w-7 h-7" on:click={goBack}>
							<img class="w-7 h-7" src="/icons/arrow-left.svg" alt="arrow-left" />
						</button>
					</div>
				{/if}
				{#if !isFinalPage && !isSigninPage}
					<div class="flex items-center ml-3 gap-10">
						<p class="text-lg text-shark font-jakarta-bold">Registration</p>
						<div class="hidden md:block">
							<StageHeader />
						</div>
					</div>
				{/if}
			</div>
		{/if}
		<div class="items-center gap-5 flex">
			<div class="hidden md:gap-5 md:grid md:grid-cols-2">
				{#if isClassPage}
					{#each titles as title}
						<button
							on:click={() => featureHandler(title)}
							class={classNames(
								'bg-alabaster py-[10px] px-6 rounded-full border-2 border-alabaster text-nevada text-sm font-jakarta-bold flex items-center gap-2 justify-center w-[142px]',
								selectedTitle === title &&
									'!border-affair !bg-affair-light !text-affair !font-jakarta-bold'
							)}
						>
							{#if title === 'Calendar'}
								{#if selectedTitle === title}
									<img class="h-5" src="/icons/calendar-purple.svg" alt="calendar" />
								{:else}
									<img class="h-5" src="/icons/calendar-gray.svg" alt="calendar" />
								{/if}
							{:else if selectedTitle === title}
								<img class="h-6" src="/icons/list-purple.svg" alt="list" />
							{:else}
								<img class="h-6" src="/icons/list-gray.svg" alt="list" />
							{/if}
							{title}
						</button>
					{/each}
				{/if}
			</div>
			<div class="relative">
				<button
					class="w-[50px] h-[50px] bg-alabaster flex items-center justify-center rounded-full"
					on:click={() => (showRegisterContainer = !showRegisterContainer)}
				>
					<img class="w-8 h-8" src="/icons/chess-icon.svg" alt="chess" />
				</button>
				{#if showRegisterContainer}
					<AccountModal on:closeRegister={() => (showRegisterContainer = false)} />
				{/if}
			</div>
		</div>
	</div>
</header>

{#if isFormPages && !isFinalPage}
	<div class="block md:hidden mb-3">
		<StageHeader />
	</div>
{/if}

<div class="md:hidden gap-5 grid grid-cols-2 w-[250px] mb-3">
	{#if isClassPage}
		{#each titles as title}
			<button
				type="button"
				on:click={() => featureHandler(title)}
				class={classNames(
					'bg-alabaster py-[6px] rounded-full border-2 border-alabaster text-nevada text-sm font-jakarta-bold flex items-center gap-2 justify-center',
					'px-3',
					selectedTitle === title &&
						'!border-affair !bg-affair-light !text-affair !font-jakarta-bold'
				)}
			>
				{#if title === 'Calendar'}
					{#if selectedTitle === title}
						<img class="h-5" src="/icons/calendar-purple.svg" alt="calendar" />
					{:else}
						<img class="h-5" src="/icons/calendar-gray.svg" alt="calendar" />
					{/if}
				{:else if selectedTitle === title}
					<img class="h-6" src="/icons/list-purple.svg" alt="list" />
				{:else}
					<img class="h-6" src="/icons/list-gray.svg" alt="list" />
				{/if}
				{title}
			</button>
		{/each}
	{/if}
</div>
